import React, { useState, useRef, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'gatsby'
import { Alert, Col, Row, Stack } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import ReCapture from 'react-google-recaptcha'
import { Checkbox, TextInput, IconButton } from '/src/components/common'
import { getCart } from '/src/hooks/useCart'
import { postRequest } from '/src/httpClient'

import './registration_form.scss'

const PasswordStrengthIndicator = ({ strength }) => {
  return (
  <div className="password-strength-indicator mb-3">
    <div className="meter-fill" data-strength={strength}></div>
  </div>
  )
}

export const RegistrationForm = () => {
  const { register, formState: { errors }, setError, handleSubmit } = useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState(false)
  const [validatePassword, setValidatePassword] = useState({
    hasNumber: false,
    has8digit: false
  })
  const passwordStrength = useMemo(() => Object.values(validatePassword).reduce((a, item) => a + item, 0), [validatePassword])
  const captchaRef = useRef()

  const onSubmit = (data) => {
    if (!captchaRef.current.getValue()) {
      setMessage('Captcha incorrect, please try again.')
      return
    }

    setIsLoading(true)
    postRequest('/api/user/register', {...data, captcha: captchaRef.current.getValue()}).then(res => {
      setIsLoading(false)

      if (!res) {
        setError('email', { type: 'custom', message: 'Sorry, unable to register. An account may already exist with this email address.' })
      }
      else {
        setMessage('Registration successful, logging in...')

        setTimeout(() => {
          getCart().then(cartRes => {
            if (!cartRes) {
              window.location.href = '/user'
            }
            else {
              const { order_items } = cartRes.relationships

              if (!cartRes || cartRes === null || (cartRes && order_items.data.length === 0)) {
                window.location.href = '/user'
              }
              else {
                window.location.href = '/cart'
              }
            }
          })
        }, 1000)

        /*setTimeout(() => {
          getRequest('/api/user', new AbortController()).then(userRes => {
            getCart().then(cartRes => {
              if (!cartRes) {
                navigate('/user')
                return
              }
              const { order_items } = cartRes.relationships

              resetCart()
              setUserState(userRes)
              setCartState(cartRes)

              let cartQuantity = 0
              if (order_items.data.length !== 0) {
                cartQuantity = order_items.data.map(({ attributes }) => {
                  return attributes.quantity
                }).reduce(
                  (a, c) => a + parseInt(c),
                  0
                )
              }
              setCartCount(cartQuantity)
  
              if (!cartRes || cartRes === null || (cartRes && order_items.data.length === 0)) {
                navigate('/user')
              }
              else {
                navigate('/cart')
              }

              setIsLoading(false)
            })
          })
        }, 500)*/
      }
    })
  }

  return (
    <div className="registration-form-container">
      <form id="registration-form" onSubmit={handleSubmit(onSubmit)}>
        <Row className="mb-4">
          {message !== false && (
            <Col sm={12}>
              <Alert variant="success">
                {message}
              </Alert>
            </Col>
          )}
          <Col sm={6}>
            <TextInput label="First name" type="text" error={errors.first_name} register={register('first_name', {
              required: true
            })} />
          </Col>
          <Col sm={6}>
            <TextInput label="Last name" type="text" error={errors.last_name} register={register('last_name', {
              required: true
            })} />
          </Col>
          <Col sm={12}>
            <TextInput label="Email" type="text" error={errors.email} register={register('email', {
              required: true,
              pattern: {
                value: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
                message: 'Please provide a valid email address'
              }
            })} />
          </Col>
          <Col sm={12}>
            <TextInput label="Phone number" type="text" error={errors.phone_number} register={register('phone_number', {
              required: true
            })} />
          </Col>
          <Col sm={6}>
            <TextInput label="Password" type="password" error={errors.pass} register={register('pass', {
              required: true,
              pattern: {
                value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d\S]{8,}$/,
                message: 'Password must be at least 8 characters long and contain at least one number'
              },
              onChange: (event) => {
                const { value } = event.target
                if (value.match(/\d+/g)) {
                  setValidatePassword((s) => ({ ...s, hasNumber: true }))
                }
                else {
                  setValidatePassword((s) => ({ ...s, hasNumber: false }))
                }

                if (value.length >= 8) {
                  setValidatePassword((s) => ({ ...s, has8digit: true }))
                }
                else {
                  setValidatePassword((s) => ({ ...s, has8digit: false }))
                }
              }
            })} />
          </Col>
          <Col sm={6}>
            <TextInput label="Confirm Password" type="password" error={errors.confirm_pass} register={register('confirm_pass', {
              required: true
            })} />
          </Col>
          <Col sm={12}>
            <PasswordStrengthIndicator strength={passwordStrength} />
            <p className="description mb-4">To meet password strength requirements, please ensure your password has at least 8 characters and contains at least 1 number.</p>
          </Col>
          <Col sm={12}>
            <Checkbox 
              label="I want to receive the Metalcorp newsletter" 
              type="checkbox" 
              error={errors.newsletter} 
              register={register('newsletter', {
                required: false
              })
            } />
          </Col>
          <Col sm={12}>
            <Checkbox 
              label="I agree with the Privacy Policy, Terms & Conditions and Conditions of Sale" 
              type="checkbox" 
              error={errors.policy} 
              register={register('policy', {
                required: true
              })
            } />
          </Col>
          <Col sm={12}>
            <ReCapture ref={captchaRef} sitekey="6LcobZUjAAAAAA_AbX6SZWgpVTCL_nXZfoZeHOL5" />
          </Col>
        </Row>
        <Row>
          <Stack direction="horizontal" gap={5}>
            <IconButton type="submit" icon={faArrowRight} disabled={isLoading}>
              {isLoading ? 'Checking...' : 'Register'}
            </IconButton>
            <Link className="link forgot-password-link" to="/user/password">
              <FontAwesomeIcon icon={faQuestionCircle} />
              Forgot Password
            </Link>
          </Stack>
        </Row>
      </form>
    </div>
  )
}

export default RegistrationForm